import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import PageLayout from "../components/PageLayout";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { FaRegBuilding, FaChevronRight } from "react-icons/fa";
import { FaStar } from "react-icons/fa6";
import ManufacturerAPI from "../api/manufacturer";
import { useSearchParams } from "react-router-dom";
import SponsersAffiliationsSidebar from "../components/SponsersAffiliationsSidebar";
import ManufactorerCard from "../components/manufactorers/ManufactorerCard";

export default function Manufacturer() {
    const location = useLocation();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [manufacturers, setManufacturers] = useState([]);
    const [manufacturersFeature, setManufacturersFeature] = useState([]);
    const [pagination, setPagination] = useState({
        page: 1,
        totalItems: 0,
        totalPages: 0
    });
    const [currentPage, setCurrentPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    const [searchQuery, setSearchQuery] = useState(searchParams.get('q') || '');
    const observer = useRef();
    const lastItemRef = useCallback(node => {
        if (isLoading) return;
        if (observer.current) observer.current.disconnect();
        observer.current = new IntersectionObserver(entries => {
            if (entries[0].isIntersecting && hasMore) {
                setCurrentPage(prevPage => prevPage + 1);
            }
        });
        if (node) observer.current.observe(node);
    }, [isLoading, hasMore]);

    const alphabetData = useMemo(() => {
        const alphabet = '#ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');
        const groups = {};
        alphabet.forEach(letter => {
            if (letter === '#') {
                groups[letter] = manufacturers.filter(item =>
                    !/^[A-Za-z]$/.test(item.name)
                );
            } else {
                groups[letter] = manufacturers.filter(item =>
                    item.name.toUpperCase().startsWith(letter)
                );
            }
        });
        const validLetters = alphabet.filter(letter => groups[letter].length > 0);
        return { alphabet, groups, validLetters, totalItems: pagination.totalItems };
    }, [manufacturers, pagination.totalItems]);

    const [activeLetter, setActiveLetter] = useState(() => {
        const letterFromUrl = searchParams.get('letter');
        if (letterFromUrl && alphabetData.groups[letterFromUrl]?.length > 0) {
            return letterFromUrl;
        }
        return alphabetData.validLetters[0] || 'A';
    });

    const fetchManufacturers = useCallback(async (letter, page) => {
        try {
            setIsLoading(true);
            setError(null);
            const data = await ManufacturerAPI.getByLetterPage(letter, page);
            if (page === 1) {
                setManufacturers(data.results || []);
            } else {
                setManufacturers(prevItems => [...prevItems, ...(data.results || [])]);
            }
            setPagination(data.pagination || { page: 1, totalItems: 0, totalPages: 0 });
            setHasMore(data.pagination?.page < data.pagination?.totalPages);
        } catch (err) {
            setError('Failed to load manufacturers. Please try again later.');
            console.error('Error fetching manufacturers:', err);
        } finally {
            setIsLoading(false);
        }
    }, []);

    const fetchManufacturersFeature = useCallback(async () => {
        try {
            const data = await ManufacturerAPI.getFeatures();
            setManufacturersFeature(data.results || []);
        } catch (err) {
            console.error('Error fetching featured manufacturers:', err);
        }
    }, []);

    useEffect(() => {
        setManufacturers([]);
        setCurrentPage(1);
        setHasMore(true);
        fetchManufacturers(activeLetter, 1);
        fetchManufacturersFeature();
    }, [activeLetter, fetchManufacturers, fetchManufacturersFeature]);

    useEffect(() => {
        if (currentPage > 1) {
            fetchManufacturers(activeLetter, currentPage);
        }
    }, [currentPage, activeLetter, fetchManufacturers]);

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        params.set('letter', activeLetter);
        navigate({
            pathname: location.pathname,
            search: params.toString()
        }, { replace: true });
    }, [activeLetter, navigate, location.pathname, location.search]);

    const handleSearch = async (e) => {
        e.preventDefault();
        try {
            setIsLoading(true);
            setError(null);
            const data = await ManufacturerAPI.search(searchQuery);
            setManufacturers(data.results || []);
            setPagination(data.pagination || { page: 1, totalItems: 0, totalPages: 0 });
            setCurrentPage(1);
            setHasMore(false);
        } catch (err) {
            setError('Search failed. Please try again later.');
            console.error('Error searching manufacturers:', err);
        } finally {
            setIsLoading(false);
        }
    };

    const switchToLetter = (letter) => {
        const params = new URLSearchParams(location.search);
        params.set('letter', letter);
        navigate({
            pathname: location.pathname,
            search: params.toString()
        }, { replace: true });
        setActiveLetter(letter);
        setManufacturers([]);
        setCurrentPage(1);
        setHasMore(true);
        fetchManufacturers(letter, 1);
    };

    if (error) {
        return (
            <PageLayout
                title="Pharmaceutical"
                titleHighlight="Manufacturers"
                subtitle="Browse our comprehensive database of pharmaceutical manufacturers"
                showSearch={true}
                pageTitle="Manufacturers"
                initialType="manufacturers"
                showGradient={true}
            >
                <div className="p-8 text-center border bg-white/60 border-white/40 rounded-2xl mt-6">
                    <div className="mb-4 text-16 text-red-600 font-medium">{error}</div>
                    <button
                        onClick={() => window.location.reload()}
                        className="px-5 py-2.5 text-14 font-medium text-white bg-purple-600 rounded-lg hover:bg-purple-700 transition-colors min-h-[44px]"
                    >
                        Try Again
                    </button>
                </div>
            </PageLayout>
        );
    }

    return (
        <PageLayout
            title="Pharmaceutical"
            titleHighlight="Manufacturers"
            subtitle="Browse our comprehensive database of pharmaceutical manufacturers"
            showSearch={true}
            pageTitle="Manufacturers"
            initialType="manufacturers"
            showGradient={true}
            onSearch={handleSearch}
            searchValue={searchQuery}
            onSearchChange={(e) => setSearchQuery(e.target.value)}
        >
            {/* === TOP PHARMACY: Featured Section === */}
            {manufacturersFeature.length > 0 && (
                <section className="mt-6 mb-8" aria-label="Top pharmacy manufacturers">
                    <div className="flex items-center gap-3 mb-4">
                        <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-purple-600/10">
                            <FaStar className="size-3.5 text-purple-600" />
                            <span className="text-14 font-medium text-purple-700">Top Pharmacy</span>
                        </div>
                        <div className="flex-1 h-px bg-secondary/10" />
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
                        {manufacturersFeature.slice(0, 8).map((item, index) => (
                            <Link
                                key={`featured-${item.uri || index}`}
                                to={`/manufacturers/${item.uri}`}
                                className="group"
                            >
                                <div className="flex items-center gap-3 p-4 h-full rounded-xl border-2 border-purple-100 bg-gradient-to-br from-purple-50/80 to-white transition-all duration-200 hover:border-purple-300 hover:shadow-md">
                                    <div className="flex-shrink-0 flex items-center justify-center w-10 h-10 rounded-full bg-purple-600/10 text-purple-600 group-hover:bg-purple-600/20 transition-colors">
                                        <FaRegBuilding className="size-4" />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="font-medium text-15 text-secondary group-hover:text-purple-700 transition-colors truncate">
                                            {item.name.replace("&amp;", "&")}
                                        </div>
                                    </div>
                                    <FaChevronRight className="size-3 text-secondary/30 group-hover:text-purple-500 transition-all group-hover:translate-x-0.5 flex-shrink-0" />
                                </div>
                            </Link>
                        ))}
                    </div>
                </section>
            )}

            {/* === ALPHABET NAVIGATION === */}
            <div className="sticky z-10 pt-3 pb-3 top-8 backdrop-blur-md bg-[#f5f5f7]/80">
                {/* Desktop Alpha Navigation */}
                <div className="hidden md:block">
                    <div className="flex items-center gap-3">
                        <span className="text-14 font-medium text-secondary/60 flex-shrink-0">Browse A–Z</span>
                        <nav className="inline-flex flex-wrap gap-0.5 p-1 border rounded-xl bg-white/60 backdrop-blur-sm border-white/60" aria-label="Alphabetical navigation">
                            {alphabetData.alphabet.map(letter => {
                                const isActive = activeLetter === letter;
                                return (
                                    <button
                                        key={letter}
                                        onClick={() => switchToLetter(letter)}
                                        aria-current={isActive ? 'true' : undefined}
                                        className={`size-7 relative flex items-center justify-center rounded-lg text-14 font-text font-medium transition-all min-w-[28px] min-h-[28px] ${isActive
                                            ? "bg-purple-600 text-white shadow-sm"
                                            : "text-secondary hover:bg-purple-50 hover:text-purple-700"
                                        }`}
                                    >
                                        {letter}
                                    </button>
                                );
                            })}
                        </nav>
                    </div>
                </div>

                {/* Mobile Alpha Navigation */}
                <div className="md:hidden overflow-hidden">
                    <div className="flex overflow-x-auto py-1 gap-1.5 -mx-1 px-1 pb-2" role="navigation" aria-label="Alphabetical navigation">
                        {alphabetData.alphabet.map(letter => (
                            <button
                                key={letter}
                                onClick={() => switchToLetter(letter)}
                                className={`flex-shrink-0 h-9 w-9 flex items-center justify-center rounded-lg text-sm font-medium transition-colors ${activeLetter === letter
                                    ? "bg-purple-600 text-white"
                                    : "bg-white/60 text-secondary hover:bg-purple-50 border border-white/70"
                                }`}
                            >
                                {letter}
                            </button>
                        ))}
                    </div>
                </div>
            </div>

            {/* === MAIN CONTENT: Full-width grid, no sidebar === */}
            <div className="mt-4 pb-12">
                {isLoading && currentPage === 1 ? (
                    <div className="flex items-center justify-center h-48">
                        <div className="w-10 h-10 border-2 border-purple-200 border-t-purple-600 rounded-full animate-spin" />
                    </div>
                ) : alphabetData.groups[activeLetter]?.length > 0 ? (
                    <>
                        {/* Result count */}
                        <div className="flex items-center justify-between mb-4">
                            <p className="text-14 text-secondary/60">
                                <span className="font-medium text-secondary">{alphabetData.groups[activeLetter].length}</span> manufacturers starting with "{activeLetter}"
                            </p>
                        </div>

                        {/* Grid — full width, no sidebar stealing space */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
                            {alphabetData.groups[activeLetter].map((item, index) => {
                                const isLastItem = index === alphabetData.groups[activeLetter].length - 1;
                                if(item.status !== 'active') return;
                                return (
                                    <React.Fragment key={`manufacturer-${item.uri || index}`}>
                                        <ManufactorerCard
                                            item={item}
                                            isLastItem={isLastItem}
                                            lastItemRef={lastItemRef}
                                            isFeature={item.is_top_manufacturer || item.is_sponsor}
                                            description={item.description}
                                        />
                                        {/* Mobile sponsor insertion every 12 items */}
                                        {index > 0 && index % 12 === 0 && (
                                            <div className="col-span-1 sm:col-span-2 lg:col-span-3 xl:col-span-4 block md:hidden py-2">
                                                <SponsersAffiliationsSidebar showRange={index / 12 % 2 === 0 ? {start: 0, end: 3} : {start: 3, end: 6}} />
                                            </div>
                                        )}
                                    </React.Fragment>
                                );
                            })}
                        </div>

                        {/* Loading more indicator */}
                        {isLoading && currentPage > 1 && (
                            <div className="flex items-center justify-center py-6">
                                <div className="w-8 h-8 border-2 border-purple-200 border-t-purple-600 rounded-full animate-spin" />
                            </div>
                        )}
                    </>
                ) : (
                    <div className="p-10 text-center border bg-white/40 border-white/40 rounded-2xl">
                        <div className="inline-flex items-center justify-center w-14 h-14 mb-4 text-22 font-bold rounded-full bg-purple-50 text-purple-600/70">
                            {activeLetter}
                        </div>
                        <h3 className="text-16 font-medium text-secondary">No manufacturers found</h3>
                        <p className="mt-2 text-14 text-secondary/60">
                            Try another letter from the navigation above
                        </p>
                    </div>
                )}
            </div>
        </PageLayout>
    );
}
